import { toRaw, isProxy } from 'vue';

/**
 * Groups and sorts parameter headings from product search results.
 *
 * @param {SearchResult[]} products - Array of product search results.
 * @param {boolean} [includeSecondary=true] - Whether to include secondary parameters.
 * @returns {{ data: Object[]; sorting: number; groupTitle: string }[]}
 */
export default function getParameterHeadings(products, includeSecondary = true) {
    const getOrCreateGroup = (parameter, groups) => {
        let group = groups.find(g => g.groupTitle === parameter.grouping);
        if (!group) {
            group = {
                groupTitle: parameter.grouping,
                data: [],
                sorting: parameter.sorting,
            };
            groups.push(group);
        }
        return group;
    };

    const addParameterToGroup = (parameter, group) => {
        group.sorting = Math.min(group.sorting, parameter.sorting);

        if (!group.data.some(p => p.id === parameter.id)) {
            group.data.push({
                id: parameter.id,
                name: parameter.name,
                prefix: parameter.prefix,
                symbol: parameter.symbol.replaceAll('_sub', 'sub'),
                fieldName: parameter.fieldName,
                sorting: parameter.sorting,
            });
        }
    };

    const collectGroups = (products, includeSecondary = false) => {
        const groups = [];

        for (const product of products) {
            let grouped = product.groupedParameters;

            if (!grouped) continue;

            // Unwrap Vue proxy if necessary
            if (isProxy(grouped)) {
                grouped = toRaw(grouped);
            }

            // If it's an array (legacy format), wrap as needed
            let parametersArray;
            if (Array.isArray(grouped)) {
                parametersArray = Array.isArray(grouped[0])
                    ? grouped // array of arrays
                    : [grouped];
            }
            // If it's an object (new format), use Object.values
            else if (typeof grouped === 'object') {
                parametersArray = Object.values(grouped);
            } else {
                continue; // skip unexpected formats
            }

            for (const paramGroup of parametersArray) {
                // Sometimes paramGroup can still be a single object
                const params = Array.isArray(paramGroup) ? paramGroup : [paramGroup];

                for (const parameter of params) {
                    if (includeSecondary || parameter.priority === 'primary') {
                        const group = getOrCreateGroup(parameter, groups);
                        addParameterToGroup(parameter, group);
                    }
                }
            }
        }

        return groups;
    };

    const sortGroupsAndItems = groups =>
        groups
            .sort((a, b) => a.sorting - b.sorting)
            .map(group => ({
                ...group,
                data: group.data.sort((a, b) => a.sorting - b.sorting),
            }));

    const groups = collectGroups(products, includeSecondary);
    return sortGroupsAndItems(groups);
}

